import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Sparkles } from 'lucide-react';
import { profileData } from '../data/mock';

const PROFILE_IMAGE = "https://customer-assets.emergentagent.com/job_motion-profile-3/artifacts/mxrz3tmc_Screenshot%202026-02-01%20at%202.22.10%E2%80%AFPM.png";

// Floating Orbs Component - Warm tones only (no blue)
const FloatingOrbs = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Warm peach orb */}
      <motion.div
        animate={{
          y: [0, -30, 0],
          x: [0, 20, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/4 left-1/5 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#ffb79c]/20 to-[#ffd4c4]/10 blur-3xl"
      />
      {/* Soft gold orb */}
      <motion.div
        animate={{
          y: [0, 20, 0],
          x: [0, -15, 0],
          scale: [1, 1.15, 1],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute top-1/3 right-1/4 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-[#f5d89a]/20 to-[#ffe4b8]/10 blur-3xl"
      />
      {/* Dusty rose orb */}
      <motion.div
        animate={{
          y: [0, -25, 0],
          x: [0, 10, 0],
          scale: [1, 1.08, 1],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        className="absolute bottom-1/4 left-1/3 w-[350px] h-[350px] rounded-full bg-gradient-to-br from-[#e8c4c4]/15 to-[#f0d4d4]/10 blur-3xl"
      />
      {/* Soft rose accent */}
      <motion.div
        animate={{
          y: [0, 15, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        className="absolute bottom-1/3 right-1/5 w-[250px] h-[250px] rounded-full bg-gradient-to-br from-[#f4b8c5]/15 to-transparent blur-3xl"
      />
    </div>
  );
};

const ChatBubble = ({ message, index, isVisible }) => {
  const variants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
        delay: message.delay / 1000
      }
    }
  };

  const getBubbleStyle = () => {
    switch (message.type) {
      case 'greeting':
        return 'bg-gradient-to-r from-[#ffb79c]/20 to-[#ffd4c4]/10 border border-[#ffb79c]/30';
      case 'highlight':
        return 'bg-gradient-to-r from-[#f5d89a]/20 to-[#e8c4c4]/10 border border-[#f5d89a]/30';
      case 'cta':
        return 'bg-transparent text-white/50 text-sm';
      default:
        return 'bg-white/5 border border-white/10';
    }
  };

  return (
    <motion.div
      variants={variants}
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
      className={`rounded-2xl px-6 py-3 max-w-md backdrop-blur-sm ${getBubbleStyle()}`}
    >
      {message.type === 'greeting' && (
        <span className="text-2xl mr-2">👋</span>
      )}
      <span className={`${message.type === 'highlight' ? 'text-[#f5d89a] font-semibold tracking-wide' : 'text-white'}`}>
        {message.text.replace('👋', '')}
      </span>
    </motion.div>
  );
};

const ConversationalHero = () => {
  const [visibleMessages, setVisibleMessages] = useState(0);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleMessages((prev) => {
        if (prev < profileData.conversationIntro.length) {
          return prev + 1;
        }
        clearInterval(timer);
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (visibleMessages === profileData.conversationIntro.length) {
      setTimeout(() => setShowScrollIndicator(true), 500);
    }
  }, [visibleMessages]);

  return (
    <section className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#0d0d0f]">
      {/* Animated Floating Orbs */}
      <FloatingOrbs />

      {/* Subtle Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}
      />

      {/* Noise texture */}
      <div className="absolute inset-0 noise-overlay" />

      <div className="relative z-10 px-6 py-20 w-full max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Content */}
          <div>
            {/* Conversation Container */}
            <div className="space-y-4">
              <AnimatePresence>
                {profileData.conversationIntro.slice(0, visibleMessages).map((message, index) => (
                  <ChatBubble
                    key={index}
                    message={message}
                    index={index}
                    isVisible={true}
                  />
                ))}
              </AnimatePresence>
            </div>

            {/* Main Title - appears after chat */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{
                opacity: visibleMessages >= 3 ? 1 : 0,
                y: visibleMessages >= 3 ? 0 : 40
              }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-12 space-y-6"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight">
                <span className="block">Building</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#ffb79c] via-[#f5d89a] to-[#f4b8c5] animate-gradient bg-[length:200%_auto]">
                  Intelligent Systems
                </span>
              </h1>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: visibleMessages >= 4 ? 0.7 : 0 }}
                transition={{ delay: 0.3 }}
                className="text-white/60 text-lg md:text-xl max-w-xl leading-relaxed"
              >
                {profileData.about}
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: visibleMessages >= 4 ? 1 : 0, y: visibleMessages >= 4 ? 0 : 20 }}
                transition={{ delay: 0.5 }}
                className="flex flex-wrap gap-4 pt-4"
              >
                <motion.a
                  href="#projects"
                  className="px-8 py-4 bg-gradient-to-r from-[#ffb79c] to-[#ffd4c4] text-[#0d0d0f] font-semibold rounded-full hover:shadow-lg hover:shadow-[#ffb79c]/25 transition-all inline-flex items-center gap-2"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <Sparkles size={18} />
                  View My Work
                </motion.a>
                <motion.a
                  href="#contact"
                  className="px-8 py-4 border border-white/20 text-white font-semibold rounded-full hover:bg-white/5 hover:border-[#f5d89a]/40 transition-all"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Let's Connect
                </motion.a>
              </motion.div>
            </motion.div>
          </div>

          {/* Right Side - Profile Photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, x: 50 }}
            animate={{ 
              opacity: visibleMessages >= 2 ? 1 : 0, 
              scale: visibleMessages >= 2 ? 1 : 0.9,
              x: visibleMessages >= 2 ? 0 : 50
            }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:flex justify-center items-center"
          >
            <div className="relative">
              {/* Soft Glowing Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#ffb79c]/30 via-[#f5d89a]/20 to-[#f4b8c5]/30 rounded-full blur-3xl scale-125 animate-pulse-glow" />
              
              {/* Animated Ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="absolute -inset-6 rounded-full"
                style={{
                  background: 'conic-gradient(from 0deg, transparent, rgba(255,183,156,0.3), transparent, rgba(245,216,154,0.3), transparent)',
                }}
              />
              
              {/* Second Ring */}
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                className="absolute -inset-3 rounded-full border border-dashed border-white/10"
              />
              
              {/* Profile Image Container */}
              <div className="relative w-80 h-80 xl:w-96 xl:h-96 rounded-full overflow-hidden border-2 border-white/10 shadow-2xl shadow-[#ffb79c]/10">
                <img 
                  src={PROFILE_IMAGE}
                  alt="Sai Pavan Tej Bobba"
                  className="w-full h-full object-cover object-top"
                />
                
                {/* Soft Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0f]/50 via-transparent to-transparent" />
              </div>

              {/* Floating Badge - Open to opportunities */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: visibleMessages >= 3 ? 1 : 0,
                  y: visibleMessages >= 3 ? 0 : 20
                }}
                transition={{ delay: 0.8 }}
                className="absolute -bottom-4 -right-4 bg-[#0d0d0f]/90 backdrop-blur-xl border border-[#f5d89a]/30 rounded-2xl px-4 py-3 shadow-xl shadow-[#f5d89a]/10"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 bg-[#f5d89a] rounded-full animate-pulse" />
                  <span className="text-white/90 text-sm font-medium">Open to opportunities</span>
                </div>
              </motion.div>

              {/* Tech Stack Badge */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ 
                  opacity: visibleMessages >= 4 ? 1 : 0,
                  y: visibleMessages >= 4 ? 0 : -20
                }}
                transition={{ delay: 1 }}
                className="absolute -top-2 -left-4 bg-[#0d0d0f]/90 backdrop-blur-xl border border-[#e8c4c4]/30 rounded-2xl px-4 py-2 shadow-xl"
              >
                <span className="text-[#e8c4c4] text-xs font-medium">AI/ML • Cloud • Backend</span>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <AnimatePresence>
          {showScrollIndicator && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
            >
              <span className="text-white/40 text-sm">Scroll to explore</span>
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ChevronDown className="text-[#ffb79c]/60" size={24} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ConversationalHero;
